import React from 'react';
import styles from './styles.module.scss';
import classnames from 'classnames';
import moment from 'moment';
import colorVariables from '@densityco/ui/variables/colors.json';

import { Sensor, Note, NoteAction } from '../../types/api';

// A component which renders information about where the sensor is in the sensor state machine, but
// for a less technical audience
const SetupStepInfo: React.FunctionComponent<{
  currentState: Sensor['current_state'],
  currentStateMetadata: Sensor['current_state_metadata'],
  onClickNoteAction?: (noteId: Note['id'], action: NoteAction) => void;
}> = ({ currentState, currentStateMetadata, onClickNoteAction }) => {
  if (currentState === null) {
    return (
      <span style={{color: colorVariables.gray300}}>
        {currentStateMetadata.human_readable_name}
      </span>
    );
  }

  let secondLine: React.ReactNode = null;
  if (
    onClickNoteAction &&
    currentStateMetadata.requires_human_interaction &&
    currentStateMetadata.latest_note_with_actions
  ) {
    secondLine = (
      <div
        className={styles.automaticSetupStepActionList}
        // Ensure that clicks from within this element don't make their way to the row
        // and trigger the click behavior on it
        onMouseDown={e => e.stopPropagation()}
      >
        Actions:&nbsp;
        {currentStateMetadata.latest_note_with_actions.actions.slice().reverse().map(action => {
          const contents = (
            <span
              className={classnames(styles.automaticSetupStepAction, {
                [styles.primary]: action.is_primary,
                [styles.disabled]: !action.enabled,
              })}
            >
              {action.label}
            </span>
          )

          if (action.href) {
            return (
              <a key={action.id} href={action.href}>{contents}</a>
            );
          } else {
            return (
              <span key={action.id} onClick={() => {
                if (!currentStateMetadata.requires_human_interaction) {
                  return;
                }
                onClickNoteAction(
                  currentStateMetadata.latest_note_with_actions.id,
                  action,
                );
              }}>
                {contents}
              </span>
            );
          }
        })}
      </div>
    );
  } else if (currentStateMetadata.next_state_transition_at) {
    secondLine = (
      <span className={styles.automaticSetupStepNextStateAt}>
        Check back {moment(currentStateMetadata.next_state_transition_at).fromNow()}
      </span>
    );
  }

  return (
    <div className={styles.automaticSetupStepWrapper}>
      <span className={classnames(styles.automaticSetupStepName, {
        [styles.requiresHumanInteraction]: currentStateMetadata.requires_human_interaction
      })}>
        {currentStateMetadata.human_readable_name}
      </span>
      {secondLine}
    </div>
  );
}

export default SetupStepInfo;
